/**
 * Intro Portal — landing + portal scroll experience.
 *
 * Plain script (no bundler). Loaded with `defer` from the section, which sits
 * earlier in the document than the GSAP libraries at the end of <body>; defer
 * scripts run in document order, so we wait for GSAP to be ready before init.
 *
 * Model: the portal image is full-screen behind a cream veil. The veil has an
 * arch-shaped hole (CSS mask), so at rest the visitor sees a peek of the image.
 *
 * Sequence (scrolling is LOCKED throughout the intro):
 *   1. The arch peek grows from 0 to its rest size; the image fades in with it.
 *   2. As the window just starts to open (~25% in), the Arabic logo writes on
 *      (right-to-left) via DrawSVG, revealing the solid fill behind the line.
 *   3. Just a beat after the writing begins, the two borders trace on.
 *   4. While the borders are still drawing (~half done), wordmark + "Step
 *      inside" fade in.
 *   5. "Step inside" becomes clickable. On click: ambient audio starts (a click
 *      is a reliable autoplay activation) and an auto-played, slow "move through
 *      the window" plays — the hole grows to full, the veil fades, the image
 *      scales a touch, everything else fades, the header + next section reveal —
 *      then scrolling unlocks so the visitor lands on the content.
 *
 * Respects prefers-reduced-motion: static rest state, no auto-animation, no
 * auto-sound; "Step inside" still enters (audio + instant reveal) on click.
 * Re-initialises cleanly on Shopify editor section reloads.
 */
(function () {
  var triggers = []; // ScrollTriggers we create (for editor teardown)
  var tweens = []; // timelines/tweens we create
  var cleanups = []; // misc listener removers
  var resizeBound = false;

  function ready() {
    return window.gsap && window.ScrollTrigger && window.DrawSVGPlugin;
  }

  function whenReady(cb) {
    if (ready()) return cb();
    // Poll until the GSAP libs (later in the document) have executed, so init
    // can never silently no-op if they aren't ready at DOMContentLoaded.
    var tries = 0;
    (function poll() {
      if (ready()) return cb();
      if (tries++ > 600) return; // ~10s safety cap
      requestAnimationFrame(poll);
    })();
  }

  function restHole() {
    return window.matchMedia('(min-width: 64rem)').matches ? 225 : 160;
  }

  function finalHole() {
    return Math.max(window.innerWidth, window.innerHeight) * 1.7;
  }

  function reset() {
    triggers.forEach(function (t) {
      t.kill();
    });
    tweens.forEach(function (t) {
      t.kill();
    });
    cleanups.forEach(function (fn) {
      fn();
    });
    triggers = [];
    tweens = [];
    cleanups = [];
    if (window.lenis) window.lenis.start(); // never leave scroll locked
    document.querySelectorAll('[data-intro-portal]').forEach(function (s) {
      s.__introInit = false;
    });
  }

  function setup(section) {
    if (!section || section.__introInit) return;
    section.__introInit = true;

    var borders = section.querySelector('[data-borders]');
    var outer = section.querySelector('[data-border-outer]');
    var inner = section.querySelector('[data-border-inner]');
    var bg = section.querySelector('[data-bg]');
    var fade = section.querySelector('[data-fade]'); // next-section colour backdrop
    var veil = section.querySelector('[data-veil]');
    var logo = section.querySelector('[data-logo]');
    var logoPen = section.querySelectorAll('.logo__pen'); // mask strokes (the "pen")
    var logoFinish = section.querySelector('.logo__finish'); // mask finishing reveal
    var wordmark = section.querySelector('[data-wordmark]');
    var enterGroup = section.querySelector('[data-enter-group]');
    var prompt = section.querySelector('[data-prompt]'); // "Step inside" (with audio)
    var promptSilent = section.querySelector('[data-enter-silent]'); // enter, no audio
    var scrollCue = section.querySelector('[data-scroll-cue]');
    var nextSection = document.querySelector('[data-scene]');

    // Colour the evaporate backdrop with the NEXT section's background, so the
    // image dissolves straight into it. Tracks whatever the client sets.
    var nextBg = nextSection && nextSection.getAttribute('data-ui-bg');
    if (nextBg) section.style.setProperty('--intro-fade-to', nextBg);

    // Border insets come from the CSS custom properties (--ip-border-outer /
    // --ip-border-inner) so they stay responsive (10/20 mobile, 25/50 desktop)
    // and in sync with the wordmark / enter controls, which position off the
    // same vars. Single source of truth in section-intro-portal.css.
    function insetFor(el, name, fallback) {
      var v = parseFloat(getComputedStyle(el).getPropertyValue(name));
      return isNaN(v) ? fallback : v;
    }

    function setRect(rect, inset, w, h) {
      rect.setAttribute('x', inset);
      rect.setAttribute('y', inset);
      rect.setAttribute('width', Math.max(0, w - inset * 2));
      rect.setAttribute('height', Math.max(0, h - inset * 2));
    }

    function sizeBorders() {
      var w = section.clientWidth;
      var h = section.clientHeight;
      borders.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
      setRect(outer, insetFor(section, '--ip-border-outer', 25), w, h);
      setRect(inner, insetFor(section, '--ip-border-inner', 50), w, h);
    }

    sizeBorders();

    // "Static mode": no autoplay motion. True under reduced-motion AND inside
    // the Shopify theme editor (Shopify.designMode) — so editing content shows
    // the composed rest state instead of replaying the intro on every save.
    var staticMode =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !!(window.Shopify && window.Shopify.designMode);

    // Arrived via the header logo from another page: skip the intro and land on
    // the post-intro content. Read-and-clear once so a later fresh visit replays it.
    var skipIntro = false;
    try {
      skipIntro = sessionStorage.getItem('tamre:skip-intro') === '1';
      if (skipIntro) sessionStorage.removeItem('tamre:skip-intro');
    } catch (e) {}

    function bindEnter(el, fn) {
      if (!el) return;
      el.addEventListener('click', fn);
      cleanups.push(function () {
        el.removeEventListener('click', fn);
      });
    }

    /* ---- Start the ambient audio (volume fades up) via the global
       controller. Called from "Step inside" — a reliable autoplay gesture. */
    function playAudio() {
      if (!window.tamreAudio) return;
      var el = window.tamreAudio.el;
      el.volume = 0;
      window.tamreAudio.play();
      gsap.to(el, { volume: 0.7, duration: 3.5, ease: 'none' });
    }

    // Reduced-motion / editor dissolve: a plain, low-key opacity fade from a
    // 0..1 scroll progress (no blur, no scale, no pin).
    function setDissolve(p) {
      if (!bg) return;
      bg.style.opacity = (1 - p).toFixed(3);
    }

    /* ---- Static (reduced motion / editor): rest state; buttons still enter. */
    if (staticMode) {
      if (enterGroup) enterGroup.classList.add('is-ready');
      var enteredRM = false;
      var enterRM = function (withAudio) {
        if (enteredRM) return;
        enteredRM = true;
        document.dispatchEvent(new CustomEvent('tamre:enter'));
        if (withAudio) playAudio();
        gsap.set(veil, { autoAlpha: 0 }); // reveal the full image
        if (nextSection) gsap.set(nextSection, { autoAlpha: 1 });
        // Simple scroll-driven dissolve (no pin under reduced motion).
        var rmDissolve = function () {
          setDissolve(Math.min(1, window.scrollY / (window.innerHeight * 0.8)));
        };
        rmDissolve();
        window.addEventListener('scroll', rmDissolve, { passive: true });
        cleanups.push(function () {
          window.removeEventListener('scroll', rmDissolve);
        });
      };
      bindEnter(prompt, function () { enterRM(true); });
      bindEnter(promptSilent, function () { enterRM(false); });
      if (skipIntro) enterRM(false); // logo → straight to the entered rest state
      return;
    }

    // Arrived via the logo: land instantly at the post-intro state, no autoplay.
    // Silent — there's no click gesture on this load, so audio would be blocked.
    if (skipIntro) {
      enterInstant();
      return;
    }

    // Swap CSS-translate centring for GSAP transforms (same position) so the
    // centring survives later transforms.
    gsap.set(logo, { xPercent: -50, yPercent: -50, x: 0, y: 0 });
    gsap.set([wordmark, enterGroup], { xPercent: -50, x: 0 });

    // Initial hidden / closed state.
    gsap.set(veil, { '--hole': '0px', autoAlpha: 1 });
    gsap.set(bg, { scale: 1, opacity: 0 }); // image fades in as the window opens
    gsap.set(borders, { opacity: 1 });
    gsap.set([outer, inner], { drawSVG: 0 });
    gsap.set(logo, { opacity: 1 });
    gsap.set(logoFinish, { opacity: 0 });
    // Right-to-left: reveal each stroke from its end (the right side) backward.
    // DrawSVG on a path inside a <mask> can throw in some browsers — guard it so
    // a failure falls back to a plain fade instead of killing the whole intro.
    var logoDrawOK = true;
    try {
      gsap.set(logoPen, { drawSVG: '100% 100%' });
    } catch (e) {
      logoDrawOK = false;
    }
    gsap.set(wordmark, { opacity: 0, y: -8 });
    gsap.set(enterGroup, { opacity: 0, y: 8 });
    if (scrollCue) gsap.set(scrollCue, { opacity: 0 });

    // Lock scrolling at the top during the intro — the scroll experience is
    // only built once the intro finishes (so nothing scrubs over the intro).
    window.scrollTo(0, 0);
    if (window.lenis) {
      window.lenis.scrollTo(0, { immediate: true });
      window.lenis.stop();
    }

    /* ---- Intro reveal (autoplay, not scrubbed). Deliberately slow + calm. -- */
    var intro = gsap.timeline({
      defaults: { ease: 'sine.inOut' },
      onComplete: enableEnter
    });
    tweens.push(intro);

    // 1. Peek opens AND the image fades in together — slow and calming.
    intro.to(veil, { '--hole': restHole() + 'px', duration: 2.4, ease: 'power2.out' }, 0);
    intro.to(bg, { opacity: 1, duration: 2.4, ease: 'power2.out' }, 0);

    // 2. Logo: the line draws on (right-to-left) as the window just starts to
    //    open (~25% in), revealing the fill behind it, then the finishing pass
    //    completes the mark (diacritics + ™).
    if (logoDrawOK) {
      intro.to(
        logoPen,
        {
          drawSVG: '0% 100%',
          duration: 2.8,
          ease: 'power1.inOut',
          stagger: { each: 0.35, from: 'end' }
        },
        0.55
      );
      intro.to(logoFinish, { opacity: 1, duration: 1.0, ease: 'power1.out' }, 3.2);
    } else {
      intro.to(logoFinish, { opacity: 1, duration: 1.4, ease: 'power1.out' }, 0.55);
    }

    // 3. Borders trace on, starting just a beat after the writing begins.
    intro.to(outer, { drawSVG: '100%', duration: 2.2 }, 0.75);
    intro.to(inner, { drawSVG: '100%', duration: 2.2 }, 0.95);

    // 4. Wordmark fades in with the Arabic write (they appear together); the
    //    enter group still arrives later, while the borders are still drawing.
    intro.to(wordmark, { opacity: 1, y: 0, duration: 1.1 }, 0.55);
    intro.to(enterGroup, { opacity: 1, y: 0, duration: 1.1 }, 2.3);

    /* ---- After the intro: enable the entry controls. Scroll stays locked
       until the visitor clicks one (the only way in). ----------------- */
    function enableEnter() {
      if (!enterGroup) {
        if (window.lenis) window.lenis.start();
        return;
      }
      enterGroup.classList.add('is-ready');
      bindEnter(prompt, function () { enter(true); });
      bindEnter(promptSilent, function () { enter(false); });
    }

    var entered = false;
    function enter(withAudio) {
      if (entered) return;
      entered = true;

      // Tell the header (and anything else) we've stepped into the room.
      document.dispatchEvent(new CustomEvent('tamre:enter'));

      if (withAudio) playAudio();

      // Auto-played "move through the window" — slow, like a calm slow scroll.
      var move = gsap.timeline({
        defaults: { ease: 'power1.inOut' },
        onComplete: function () {
          // Land on the content; scrolling is free from here.
          if (window.lenis) window.lenis.start();
          watchScrollCue();
          startDissolve();
        }
      });
      tweens.push(move);
      move
        // Grow the arch from the rest peek through to full screen.
        .to(veil, { '--hole': function () { return finalHole() + 'px'; }, duration: 4 }, 0)
        .to(bg, { scale: 1.08, ease: 'none', duration: 4 }, 0)
        .to([borders, logo, wordmark, enterGroup], { autoAlpha: 0, duration: 1.2 }, 0)
        // Fade the veil out so the arch's concave edges don't leave cream wedges.
        .to(veil, { autoAlpha: 0, duration: 1.2 }, 2.6);
      if (scrollCue) {
        move.to(scrollCue, { opacity: 1, duration: 1.2 }, 2.8);
      }
      if (nextSection) {
        move.fromTo(
          nextSection,
          { autoAlpha: 0 },
          { autoAlpha: 1, ease: 'none', duration: 1.2 },
          2.6
        );
      }
    }

    // Instant entry (from the header logo). Reproduces the END state of enter()'s
    // "move" timeline with no animation, then reuses the same post-enter wiring so
    // the scroll dissolve behaves identically to a normal entry. No audio.
    function enterInstant() {
      document.dispatchEvent(new CustomEvent('tamre:enter'));
      gsap.set(veil, { autoAlpha: 0 });
      gsap.set([borders, logo, wordmark, enterGroup], { autoAlpha: 0 });
      gsap.set(bg, { opacity: 1, scale: 1.08 });
      if (nextSection) gsap.set(nextSection, { autoAlpha: 1 });
      if (scrollCue) gsap.set(scrollCue, { opacity: 1 });
      window.scrollTo(0, 0);
      if (window.lenis) {
        window.lenis.scrollTo(0, { immediate: true });
        window.lenis.start();
      }
      watchScrollCue();
      startDissolve();
    }

    // Fade the scroll cue out once the visitor actually starts scrolling.
    function watchScrollCue() {
      if (!scrollCue) return;
      var onScroll = function () {
        if (window.scrollY > 30) {
          gsap.to(scrollCue, { opacity: 0, duration: 0.5 });
          window.removeEventListener('scroll', onScroll);
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      cleanups.push(function () {
        window.removeEventListener('scroll', onScroll);
      });
    }

    // Pin the image and let it EVAPORATE in place — the whole frame fades out
    // with a faint scale-up + soft blur (like mist clearing) until only the room
    // colour is left. Because the next section's colour matches, the pin release
    // is invisible: nothing slides. (Replaces the old bottom-up gradient wipe.)
    function startDissolve() {
      if (!bg) return;
      // Mobile clears the photo in even less scroll, tightening the intro→Scene
      // hand-off on phones (where the extra scroll is most felt) while keeping the
      // desktop pace the client tuned. Matches scene.js's 64rem desktop/mobile split.
      var isDesktop = window.matchMedia('(min-width: 64rem)').matches;
      var t = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          // Shorter fade distance = the photo clears in less scroll, so the Scene
          // reveal begins sooner AND, on the way back UP, the photo re-appears
          // sooner too — less cream to scroll through in both directions.
          end: isDesktop ? '+=40%' : '+=25%',
          pin: true,
          scrub: 1,
          invalidateOnRefresh: true
        }
      });
      tweens.push(t);
      triggers.push(t.scrollTrigger);
      // scale starts at 1.08 (the entry "move" left it there); explicit `from`
      // keeps the scrub reversible.
      t.fromTo(
        bg,
        { opacity: 1, scale: 1.08, y: 0, filter: 'blur(0px)' },
        { opacity: 0, scale: 1.14, y: -80, filter: 'blur(8px)', ease: 'none', duration: 1 },
        0
      );
      // Raise the next section's colour behind the image exactly as it leaves, so
      // it dissolves straight into that colour (not the room's own).
      if (fade) t.fromTo(fade, { opacity: 0 }, { opacity: 1, ease: 'none', duration: 1 }, 0);
      ScrollTrigger.refresh();
    }

    /* ---- Keep borders fitted to the viewport. ------------------------ */
    if (!resizeBound) {
      resizeBound = true;
      var refitBorders = function () {
        document.querySelectorAll('[data-intro-portal]').forEach(function (s) {
          var b = s.querySelector('[data-borders]');
          if (b && s.__introInit) {
            var w = s.clientWidth;
            var h = s.clientHeight;
            b.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
            setRect(s.querySelector('[data-border-outer]'), insetFor(s, '--ip-border-outer', 25), w, h);
            setRect(s.querySelector('[data-border-inner]'), insetFor(s, '--ip-border-inner', 50), w, h);
          }
        });
      };
      var rid;
      var onResize = function () {
        cancelAnimationFrame(rid);
        rid = requestAnimationFrame(refitBorders);
      };
      window.addEventListener('resize', onResize);
      // Refit after a REAL ScrollTrigger refresh (orientation/width change, fonts,
      // etc.) instead of forcing our own. A bare ScrollTrigger.refresh() here would
      // bypass the ignoreMobileResize guard (animations.js) and re-measure every
      // pin on each iOS address-bar show/hide — snapping scrubs and flickering the
      // sections below (see scene.js). The built-in refresh already respects it.
      ScrollTrigger.addEventListener('refresh', refitBorders);
      // Push teardown so an editor section-reload doesn't leak the listeners or
      // leave resizeBound stuck true (which would block re-binding on rebuild).
      cleanups.push(function () {
        window.removeEventListener('resize', onResize);
        ScrollTrigger.removeEventListener('refresh', refitBorders);
        resizeBound = false;
      });
    }
  }

  function initAll() {
    gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin);
    document.querySelectorAll('[data-intro-portal]').forEach(setup);
  }

  whenReady(initAll);

  // Shopify theme editor: tear down + rebuild when the section reloads, so
  // changing a setting never leaves the reveal stuck at its hidden state.
  document.addEventListener('shopify:section:load', function (e) {
    if (!ready()) return;
    if (!e.target.querySelector('[data-intro-portal]')) return;
    reset();
    document.querySelectorAll('[data-intro-portal]').forEach(setup);
  });
})();
